/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import useFormData from "@/hooks/useFormData";
import useGetMetadata from "@/hooks/useGetMetaData";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import useGetUsers from "@/hooks/useGetUsers";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@mui/material";

const FormContext = createContext();

export const MyFormData = () => useContext(FormContext);

const DefaultData = {
  work_flow_id: "",
  site_id: "",
  checklist_ids: [],
  title: "",
  description: "",
  start_datetime: "",
  hazards: [],
  allHazardsData: [],
  performing_user_ids: [],
  stepInputsData: {},
};

export const FormWrapper = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { postData, postIsLoading } = useFormData();
  const [docStatus, setDocStatus] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { toast, setToast } = useApiErrorHandler();
  const {
    WorkFlows = [],
    CheckList = [],
    Departments = [],
    SitesList = [],
    isLoading: metadataLoading,
    error: metadataError,
  } = useGetMetadata() || {};
  const { users } = useGetUsers();
  // Fetch all hazards with authentication
  const { data: allHazardsFromApi = [] } = useAuthenticatedQuery(
    ["allHazards"],
    "/api/v1/metadata/hazards",
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // Sync fetched hazards to ptwData and localStorage
  useEffect(() => {
    if (allHazardsFromApi && allHazardsFromApi.length > 0) {
      setPtwData((prev) => ({
        ...prev,
        allHazardsData: allHazardsFromApi,
      }));

      // Also store in localStorage so it persists across page reloads
      localStorage.setItem("allHazardsData", JSON.stringify(allHazardsFromApi));
    }
  }, [allHazardsFromApi]);

  const location = useLocation();

  const steps = [
    t("formWrapper.stepInfo"),
    t("formWrapper.stepResources"),
    t("formWrapper.stepHazid"),
    t("formWrapper.stepRisk"),
    t("formWrapper.stepCommitment"),
  ];
  const [currentStep, setCurrentStep] = useState(1);
  const [complete, setComplete] = useState(false);

  const [ptwData, setPtwData] = useState(() => {
    const stateData = location.state?.duplicatedData;
    if (stateData) {
      return stateData;
    }
    const duplicatedData = localStorage.getItem("duplicatedFormData");
    if (duplicatedData) {
      try {
        return JSON.parse(duplicatedData);
      } catch (error) {
        setToast({
          open: true,
          message: t("formWrapper.errorToast", { message: error.message }),
          severity: "error",
        });
      }
    }
    return DefaultData;
  });

  useEffect(() => {
    const incoming = location.state?.duplicatedData;
    let stored = null;
    if (!incoming) {
      const json = localStorage.getItem("duplicatedFormData");
      if (json) {
        try {
          stored = JSON.parse(json);
        } catch (err) {
          setToast({
            open: true,
            severity: "error",
            message: t("formWrapper.errorParseDuplicated", {
              message: err.message,
            }),
          });
        }
      }
    }
    const dataToUse = incoming || stored;
    if (dataToUse) {
      setPtwData(dataToUse);
      setIsDuplicated(true);
      setComplete(false);
      setCurrentStep(1);
      setFormSubmitted(false);
      setErrorTime("");
      // Clear file-related data when duplicating
      setSelectedFiles([]);
      setFileNames([]);
      setFileInputValues({});
      setInputValues({}); // <-- important: force re-init of dynamic step inputs

      if (incoming) {
        navigate(location.pathname, { replace: true, state: {} });
      }
      if (stored) {
        localStorage.removeItem("duplicatedFormData");
      }
    }
  }, [location.state, location.pathname, navigate]);

  const [isDuplicated, setIsDuplicated] = useState(
    !!location.state?.duplicatedData ||
      !!localStorage.getItem("duplicatedFormData")
  );

  useEffect(() => {
    if (isDuplicated && localStorage.getItem("duplicatedFormData")) {
      localStorage.removeItem("duplicatedFormData");
    }
  }, [isDuplicated]);

  const [workflowProcessed, setWorkflowProcessed] = useState(false);

  // Combined restore from localStorage - runs once on mount
  useEffect(() => {
    let updatedData = {};
    let hasUpdates = false;

    // Restore all hazards data from localStorage
    const storedAllHazards = localStorage.getItem("allHazardsData");
    if (
      storedAllHazards &&
      (!ptwData.allHazardsData || ptwData.allHazardsData.length === 0)
    ) {
      try {
        const parsedAllHazards = JSON.parse(storedAllHazards);
        updatedData.allHazardsData = parsedAllHazards;
        hasUpdates = true;
      } catch (error) {
        console.error("Failed to parse stored all hazards data:", error);
      }
    }

    // Restore workflow hazards from localStorage (for duplicated permits)
    const storedDuplicatedHazards = localStorage.getItem(
      "duplicatedWorkflowHazards"
    );
    const storedWorkflowHazards = localStorage.getItem("workflowHazards");
    const hazardsToRestore = storedDuplicatedHazards || storedWorkflowHazards;

    if (hazardsToRestore && ptwData.hazards.length === 0) {
      try {
        const parsedHazards = JSON.parse(hazardsToRestore);
        updatedData.hazards = parsedHazards;
        hasUpdates = true;

        // Clean up duplicatedWorkflowHazards after restoring
        if (storedDuplicatedHazards) {
          localStorage.removeItem("duplicatedWorkflowHazards");
        }
      } catch (error) {
        console.error("Failed to parse stored workflow hazards:", error);
      }
    }

    // Apply all updates at once
    if (hasUpdates) {
      setPtwData((prev) => ({
        ...prev,
        ...updatedData,
      }));
    } else {
      console.log("ℹ No data to restore from localStorage");
    }
  }, []);

  useEffect(() => {
    if (
      ptwData?.work_flow_id?.value &&
      WorkFlows.length &&
      !workflowProcessed &&
      isDuplicated &&
      ptwData.hazards.length === 0 // Only run if hazards weren't restored
    ) {
      handleWorkflowSelect(ptwData.work_flow_id);
      setWorkflowProcessed(true);
    } else if (
      isDuplicated &&
      ptwData.hazards.length > 0 &&
      !workflowProcessed
    ) {
      setWorkflowProcessed(true);
    } else if (
      !isDuplicated &&
      ptwData?.work_flow_id?.value &&
      WorkFlows.length &&
      !workflowProcessed &&
      ptwData.hazards.length === 0 // New permit workflow selected
    ) {
      handleWorkflowSelect(ptwData.work_flow_id);
      setWorkflowProcessed(true);
    }
  }, [
    ptwData?.work_flow_id?.value,
    WorkFlows,
    workflowProcessed,
    isDuplicated,
    ptwData.hazards.length,
  ]);

  useEffect(() => {
    if (metadataError) {
      setToast({
        open: true,
        severity: "error",
        message: t("formWrapper.errorMetadata", {
          message: metadataError.message,
        }),
      });
    }
  }, [metadataError]);

  const WorkFlowList = useMemo(
    () =>
      WorkFlows.filter((wf) => wf.target === "WORK_PERMIT").map(
        ({ id, name }) => ({
          value: id,
          label: name,
        })
      ),
    [WorkFlows]
  );

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileNames, setFileNames] = useState([]);
  const [fileInputValues, setFileInputValues] = useState({});
  const [stepInputs, setStepInputs] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [dynamicFields, setDynamicFields] = useState([]);

  const UsersList = useMemo(
    () =>
      users?.map(({ id, first_name, last_name }) => ({
        value: id,
        label: `${first_name} ${last_name}`,
      })) || [],
    [users]
  );

  const removeFile = useCallback((idx) => {
    setSelectedFiles((files) => files.filter((_, i) => i !== idx));
    setFileNames((names) => names.filter((_, i) => i !== idx));
  }, []);

  useEffect(() => {
    if (!ptwData?.work_flow_id?.value || !WorkFlows.length) return;
    const workflow = WorkFlows.find(
      (wf) => wf.id === ptwData.work_flow_id.value
    );
    if (!workflow || !workflow.steps) return;
    const firstStep = workflow.steps.find((step) => step.parent_id === null);
    const inputsArray = firstStep?.step_inputs || [];
    setDynamicFields((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(inputsArray)) {
        return prev;
      }
      return inputsArray;
    });
    const requiredStepIds = inputsArray.map((input) => input.id);
    const currentIds = Object.keys(ptwData.stepInputsData || {});
    const missingKeys = requiredStepIds.filter(
      (id) => !currentIds.includes(id)
    );
    if (inputsArray.length && (missingKeys.length > 0 || isDuplicated)) {
      const initialData = {};
      inputsArray.forEach((field) => {
        const fieldType = (field.input_type || "").toUpperCase();
        // Initialize based on field type
        if (fieldType === "BOOLEAN") {
          // Boolean: Use default_value if provided, otherwise "NO" (false)
          if (
            Object.prototype.hasOwnProperty.call(field, "default_value") &&
            field.default_value
          ) {
            const defaultVal = String(field.default_value).toUpperCase();
            if (
              defaultVal === "YES" ||
              defaultVal === "TRUE" ||
              defaultVal === "1"
            ) {
              initialData[field.id] = "YES";
            } else {
              initialData[field.id] = "NO"; // Default to NO for any other value
            }
          } else {
            initialData[field.id] = "NO"; // No default value - start with NO
          }
        } else if (fieldType === "CONFIRMATION") {
          // Confirmation: Use default_value if provided, otherwise false
          if (Object.prototype.hasOwnProperty.call(field, "default_value")) {
            const defaultVal = String(field.default_value).toLowerCase();
            initialData[field.id] = defaultVal === "true" || defaultVal === "1";
          } else {
            initialData[field.id] = false;
          }
        } else {
          // All other fields start with empty string
          initialData[field.id] = "";
        }
      });
      setPtwData((prev) => {
        if (
          JSON.stringify(prev.stepInputsData) === JSON.stringify(initialData)
        ) {
          return prev;
        }
        return {
          ...prev,
          stepInputsData: initialData,
        };
      });
    }
  }, [ptwData?.work_flow_id?.value, WorkFlows, isDuplicated]);

  const [errorTime, setErrorTime] = useState();

  const handleMultiSelect = useCallback(
    (name) => (e) =>
      setPtwData((prev) => ({
        ...prev,
        [name]: e.map((x) => ({ value: x.value, label: x.label })),
      })),
    []
  );

  const changeHandlerText = (e, field) => {
    setPtwData((prev) => ({
      ...prev,
      [field]: e.value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setFileNames(files.map((file) => file.name));
  };

  const handleSingleSelect = useCallback(
    (name) => (e) =>
      setPtwData((prev) => ({
        ...prev,
        [name]: e ? { value: e.value, label: e.label } : "",
      })),
    []
  );

  const handleDynamicChange = (fieldId, value) => {
    setPtwData((prev) => ({
      ...prev,
      stepInputsData: {
        ...prev.stepInputsData,
        [fieldId]: value,
      },
    }));
  };

  const handleWorkflowSelect = (selectedWorkflow) => {
    handleSingleSelect("work_flow_id")(selectedWorkflow);
    const workflow =
      WorkFlows.find((w) => w.id === selectedWorkflow.value) || null;

    // Get all hazards from metadata
    const allHazardsData = ptwData.allHazardsData || [];

    // Filter hazards for this workflow
    if (workflow && allHazardsData.length > 0) {
      // Get hazard IDs that belong to this workflow
      const workflowHazardIds = workflow.hazards?.map((h) => h.id) || [];

      // Filter all hazards to get the ones for this workflow and pre-select all precautions
      const workflowHazards = allHazardsData
        .filter((h) => workflowHazardIds.includes(h.id))
        .map((hazard) => {
          // Get all precaution IDs from all categories
          const allPrecautionIds = (hazard.precaution_categories || []).flatMap(
            (category) => category.precautions?.map((p) => p.id) || []
          );

          return {
            id: hazard.id,
            name: hazard.name,
            likelihood: hazard.likelihood,
            severity_people: hazard.severity_people,
            severity_asset: hazard.severity_asset,
            severity_environment: hazard.severity_environment,
            severity_reputation: hazard.severity_reputation,
            precaution_categories: hazard.precaution_categories || [],
            precaution_ids: allPrecautionIds, // Pre-select all precautions
            residual_risk: {
              hazard_id: hazard.id,
              likelihood: hazard.likelihood,
              severity_people: hazard.severity_people,
              severity_asset: hazard.severity_asset,
              severity_environment: hazard.severity_environment,
              severity_reputation: hazard.severity_reputation,
            },
          };
        });

      // Store in localStorage and state
      localStorage.setItem("workflowHazards", JSON.stringify(workflowHazards));
      setPtwData((prev) => ({
        ...prev,
        hazards: workflowHazards,
      }));
    } else {
      // No hazards for this workflow
      setPtwData((prev) => ({
        ...prev,
        hazards: [],
      }));
      localStorage.removeItem("workflowHazards");
    }

    const firstStep = workflow?.steps?.find((s) => s.parent_id === null);
    const inputsArray = firstStep?.step_inputs || [];
    setStepInputs(inputsArray);
    if (inputsArray.length) {
      localStorage.setItem("step_inputs", JSON.stringify(inputsArray));
    }
    setInputValues({});
    setFileInputValues({});
  };

  const prepareFilledStepInputs = () => {
    const inputsSource =
      dynamicFields && dynamicFields.length ? dynamicFields : stepInputs; // fallback, if any

    return inputsSource
      .map(({ id, input_type }) => {
        let raw_value = inputValues[id];
        let file_handles = (fileInputValues[id] || []).map((f) => f.name);

        const inputTypeUpper = input_type?.toUpperCase();

        if (inputTypeUpper === "TIMESTAMP" && raw_value) {
          raw_value = new Date(raw_value).toISOString();
        }

        // Handle BOOLEAN - convert YES/NO to "true"/"false" strings
        // Always send "true" or "false" (no empty strings)
        if (inputTypeUpper === "BOOLEAN") {
          if (raw_value === "YES") {
            raw_value = "true";
          } else {
            // Default to "false" for NO or any other value
            raw_value = "false";
          }
        }

        // Handle CONFIRMATION - send as "true" or "false" string
        if (inputTypeUpper === "CONFIRMATION") {
          raw_value = raw_value === true ? "true" : "false";
        }

        if (inputTypeUpper === "MULTI_USER_SELECTION") {
          if (Array.isArray(raw_value)) {
            raw_value =
              raw_value.length === 1
                ? raw_value[0]
                : raw_value.length > 1
                  ? raw_value.join(",")
                  : "";
          } else {
            raw_value = raw_value?.toString() || "";
          }
        }

        if (["MULTI_SELECTION"].includes(inputTypeUpper)) {
          raw_value = Array.isArray(raw_value) ? raw_value.join("||") : "";
        } else if (!["BOOLEAN", "CONFIRMATION"].includes(inputTypeUpper)) {
          // Don't convert BOOLEAN or CONFIRMATION to string again
          raw_value = Array.isArray(raw_value)
            ? raw_value.join(",")
            : (raw_value ?? "").toString();
        }

        return {
          step_input_id: id,
          file_handles,
          raw_value,
        };
      })
      .filter((item) => {
        const { raw_value, file_handles } = item;
        // Include items with file handles or non-empty raw_value
        const hasText =
          typeof raw_value === "string" && raw_value.trim().length > 0;
        const hasFiles = Array.isArray(file_handles) && file_handles.length > 0;
        return hasText || hasFiles;
      });
  };

  const convertToUTC = (localDateTime) => {
    if (!localDateTime) return "";
    const date = new Date(localDateTime);
    return date.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();
    const additionalData = ptwData.additional_data || {};
    const sentData = {
      work_flow_id: ptwData.work_flow_id.value,
      checklist_ids: ptwData.checklist_ids.map((c) => c.value),
      site_id: ptwData.site_id.value,
      title: ptwData.title,
      description: ptwData.description,
      start_datetime: convertToUTC(ptwData.start_datetime),
      hazards: ptwData.hazards.map((hazard) => ({
        id: hazard.id,
        precaution_ids: hazard.precaution_ids || [],
        residual_risk: hazard.residual_risk || {
          hazard_id: hazard.id,
          likelihood: null,
          severity_people: null,
          severity_asset: null,
          severity_environment: null,
          severity_reputation: null,
        },
      })),
      performing_user_ids: ptwData.performing_user_ids.map((u) => u.value),
      additional_data: additionalData,
      filled_step_inputs: prepareFilledStepInputs(),
    };

    form.append(
      "data",
      new Blob([JSON.stringify(sentData)], { type: "application/json" })
    );
    selectedFiles.forEach((file) => {
      form.append("attachments", file);
    });
    for (const key in fileInputValues) {
      fileInputValues[key].map((file) =>
        form.append("step_input_attachments", file)
      );
    }
    const url = "/api/v1/workpermits";
    try {
      await postData(form, url);

      // Immediately invalidate all document caches to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ["mydocuments"] });
      await queryClient.invalidateQueries({ queryKey: ["documents"] });

      setToast({
        open: true,
        severity: "success",
        message: t("formWrapper.successToast"),
      });
      // Clear the toast after 3 seconds
      setTimeout(() => {
        setToast((prev) => ({ ...prev, open: false }));
      }, 3000);
      setFormSubmitted(true);
      setComplete(true);
      setCurrentStep(steps.length);
      localStorage.removeItem("step_inputs");
      localStorage.removeItem("legacyPrecautions"); // Clean up legacy precautions
      // Clear all form data including files
      setPtwData(DefaultData);
      setSelectedFiles([]);
      setFileNames([]);
      setFileInputValues({});
      setInputValues({});
    } catch (error) {
      setToast({
        open: true,
        message: t("comment.errorMessage", {
          message: error.response?.data?.message || t("comment.defaultError"),
        }),
        severity: "error",
      });
      // Clear error toast after 3 seconds
      setTimeout(() => {
        setToast((prev) => ({ ...prev, open: false }));
      }, 3000);
    }
  };

  useEffect(() => {
    if (ptwData.start_datetime) {
      const selectedDateTime = new Date(ptwData.start_datetime);
      const currentDateTime = new Date();
      if (selectedDateTime < currentDateTime) {
        setErrorTime(t("formWrapper.errorTimePast"));
      } else {
        setErrorTime("");
      }
    } else {
      setErrorTime("");
    }
  }, [ptwData.start_datetime]);

  const validationFunctions = [
    () => {
      // First step validation
      const titleValid =
        typeof ptwData.title === "string" && ptwData.title.trim().length > 0;
      const descriptionValid =
        typeof ptwData.description === "string" &&
        ptwData.description.trim().length > 0;
      const workflowValid = !!ptwData.work_flow_id?.value;
      const siteValid = !!ptwData.site_id?.value;
      const performingUsersValid =
        Array.isArray(ptwData.performing_user_ids) &&
        ptwData.performing_user_ids.length > 0;
      const startDateValid = !!ptwData.start_datetime && !errorTime;

      return (
        titleValid &&
        descriptionValid &&
        workflowValid &&
        siteValid &&
        performingUsersValid &&
        startDateValid
      );
    },
    () => {
      // Second step validation - Other Resources (optional step)
      return true;
    },
    () => {
      // Third step validation - at least one hazard must be selected
      const hazardsValid =
        Array.isArray(ptwData.hazards) && ptwData.hazards.length > 0;
      return hazardsValid;
    },
    () => {
      // Fourth step validation - each hazard must have at least one precaution
      const allHazardsValid = ptwData.hazards.every(
        (hazard) => hazard.precaution_ids && hazard.precaution_ids.length > 0
      );
      return allHazardsValid;
    },
  ];

  const next = () => {
    if (validationFunctions[currentStep - 1]() && !errorTime) {
      setCurrentStep((prev) => prev + 1);
    } else {
      alert(t("formWrapper.errorValidation"));
    }
  };

  const prev = () => {
    setCurrentStep((prev) => prev - 1);
  };

  if (metadataLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography variant="body1" color="text.secondary">
          {t("formWrapper.loadingMetadata")}
        </Typography>
      </Box>
    );
  }

  return (
    <FormContext.Provider
      value={{
        currentStep,
        handleSubmit,
        steps,
        ptwData,
        setPtwData,
        handleSingleSelect,
        handleMultiSelect,
        changeHandlerText,
        errorTime,
        setErrorTime,
        handleFileChange,
        fileNames,
        handleWorkflowSelect,
        UsersList,
        WorkFlows,
        WorkFlowList,
        Departments,
        SitesList,
        CheckList,
        DefaultData,
        setFileInputValues,
        fileInputValues,
        toast,
        setToast,
        formSubmitted,
        setFormSubmitted,
        setInputValues,
        inputValues,
        handleDynamicChange,
        dynamicFields,
        next,
        prev,
        validationFunctions,
        postIsLoading,
        complete,
        setCurrentStep,
        setComplete,
        workflowProcessed,
        setWorkflowProcessed,
        removeFile,
        docStatus,
        setDocStatus,
        isDuplicated,
        setIsDuplicated,
        selectedFiles,
        setSelectedFiles,
        setFileNames,
        allHazardsFromApi,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};
